import { html, render } from "lit-html"
import { virtual } from "haunted"

import { HeaderSection, LocationInfo, DateTimeInfo } from "./components"
import { ActionButtons } from "./components/header"

import Modal from "../generic/modal"
import { EDIT_PATH, STATUSES } from "../constants"

const getDisplayStatus = (status) => {
  const statusLower = status.toLowerCase()
  return ["canceled", "deleted", "rescheduled"].includes(statusLower)
    ? status
    : ``
}

const MeetingRecurringInfo = virtual(
  ({ recurring_patterns, can_edit, url }) => {
    if (!recurring_patterns || !recurring_patterns.length) return ``

    const modalMount = document.getElementById("modal-mount")
    const onClose = () => {
      render(html``, modalMount)
    }

    const viewRecurringDetails = (e) => {
      e.stopPropagation()
      render(
        html` ${Modal({
          onClose,
          children: html`<div
            class="modal-content max-w-[680px] rounded-[20px] bg-white p-8 text-black"
          >
            <h1 class="mb-8 text-lg2 font-bold">Recurring schedule</h1>
            <div class="mb-9">
              ${recurring_patterns.map(
                (pattern, idx) => html`<div class="mb-8">
                  <h2 class="mb-4 font-bold">Recurring schedule ${idx + 1}</h2>
                  <div class="flex items-center">
                    <i class="fas fa-rotate mr-4 text-gray-450"></i>
                    <span>${pattern.description}</span>
                  </div>
                </div>`
              )}
            </div>
            <div class="flex justify-end items-center gap-x-6">
              ${can_edit
                ? html` <a
                    href=${`${url}${EDIT_PATH}`}
                    class="button tertiary shrink-0 grow-0 basis-[180px] h-11"
                  >
                    Edit meeting
                  </a>`
                : ""}
              <button
                @click=${onClose}
                class="button primary shrink-0 grow-0 basis-[180px] h-11"
              >
                OK
              </button>
            </div>
          </div>`,
        })}`,
        modalMount
      )
    }

    return html` <span class="tooltip-icon mr-2">
      <i class="fas fa-rotate text-orange-800"></i>
      <span class="tooltip-icon-tooltiptext">
        Recurring meeting.
        <button class="underline" @click=${viewRecurringDetails}>More</button>
      </span>
    </span>`
  }
)

const MeetingRow = virtual(
  ({
    id,
    name,
    url,
    status,
    agency,
    start_date,
    start_time,
    end_time,
    shortened_timezone,
    tags,
    remote,
    link,
    location,
    gcal_link,
    is_manually_added,
    recurring_patterns,
    can_edit,
    can_clone,
  }) => {
    const $filterMount = document.getElementById("filter-mount")
    const iconIcalSrc = $filterMount.dataset.iconIcal
    const iconGoogleCalendarSrc = $filterMount.dataset.iconGoogleCalendar

    const meetingStatus = getDisplayStatus(status)
    const canceled = status === STATUSES.CANCELED
    const primaryTags = tags
      .filter((tag) => !("primary" in tag) || tag.primary)
      .map((tag) => tag.name)

    return html`
      <div
        class="meeting group filter-result-card cursor-pointer ${canceled
          ? `inactive`
          : ``}"
        role="button"
        @click=${() => {
          window.location.href = url
        }}
      >
        ${meetingStatus || primaryTags.length
          ? html`${HeaderSection(
              {
                status: meetingStatus,
                tags: primaryTags,
                id,
                url,
                can_edit,
                can_clone,
                can_convert_to_assignment: can_edit,
                iconIcalSrc,
                iconGoogleCalendarSrc,
                gcal_link,
              },
              true
            )}`
          : ""}
        <div class="mb-5 flex flex-col-reverse md:flex-row justify-between">
          <p class="title text-lg text-blue group-hover:text-black font-bold">
            ${name}
          </p>
          ${meetingStatus || primaryTags.length
            ? ""
            : html`<div class="ml-auto mb-5 md:mb-0">
                ${ActionButtons({
                  id,
                  url,
                  can_edit,
                  can_clone,
                  can_convert_to_assignment: can_edit,
                  iconIcalSrc,
                  iconGoogleCalendarSrc,
                  gcal_link,
                  expanded: true,
                })}
              </div>`}
        </div>
        <div class="flex flex-wrap leading-loose">
          <div class="mr-6 flex items-center text-gray-interactive">
            ${is_manually_added
              ? html`<span
                  class="tooltip-icon w-8 h-8 rounded-md bg-gray-300 flex items-center justify-center"
                >
                  <span class="font-bold">M</span>
                  <span class="tooltip-icon-tooltiptext">Manually added</span>
                </span>`
              : html`<span class="tooltip-icon">
                  <i class="cb fa-scraper-robot text-sm"></i>
                  <span class="tooltip-icon-tooltiptext">Added by scraper</span>
                </span>`}
          </div>
          ${LocationInfo({ location, link, remote, canceled })}
          ${DateTimeInfo({
            date: start_date,
            start_time,
            end_time,
            shortened_timezone,
            recurring_info: recurring_patterns?.length
              ? MeetingRecurringInfo({
                  recurring_patterns,
                  can_edit,
                  url,
                })
              : null,
            canceled,
            agency,
          })}
        </div>
      </div>
    `
  }
)

export default MeetingRow
